// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-20
// protected: NO
// =============================================================================
// Arsip Dokumen (C5): list + filter (jenis/tanggal) + search + download (R2 presigned).
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FolderArchive, Loader2, Download, Eye, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { TableScroll } from "@/v2/components/TableScroll";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";

const ALL = "ALL";

const fmtDate = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};
const fmtSize = (b) => (b > 1024 ? `${(b / 1024).toFixed(0)} KB` : `${b} B`);

export default function ArsipPage() {
  const { api } = useV2Auth();
  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlId, setDlId] = useState(null);
  const [pvId, setPvId] = useState(null);
  const [docType, setDocType] = useState(ALL);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // Paginasi (Slice S-E): reuse <ListPager> + loadStoredPageSize (pola exchanges/S-D).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize("archive"));
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const listTopRef = useRef(null);
  const pageRef = useRef(1);

  const loadList = useCallback(async (p, size = pageSize) => {
    const target = p || pageRef.current || 1;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (docType !== ALL) params.set("doc_type", docType);
      if (q.trim()) params.set("q", q.trim());
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      params.set("page", target);
      params.set("page_size", size);
      const r = await api(`/api/v2/archive?${params.toString()}`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setTypes(d.types || []);
        setItems(d.items || []);
        setMeta({ total: d.total || 0, pages: d.pages || 1 });
        setPage(d.page || target); pageRef.current = d.page || target;
      } else { setItems([]); toast.error("Gagal memuat arsip"); }
    } finally { setLoading(false); }
  }, [api, docType, q, from, to, pageSize]);

  // Filter (jenis/tanggal/cari) berubah -> selalu kembali ke halaman 1.
  useEffect(() => { loadList(1); }, [loadList]);

  const goPage = (p) => {
    loadList(p);
    if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const changePageSize = (s) => { setPageSize(s); loadList(1, s); };

  const reset = () => { setDocType(ALL); setQ(""); setFrom(""); setTo(""); };

  const download = async (row) => {
    setDlId(row.id);
    try {
      const r = await api(`/api/v2/archive/${row.id}/download`);
      const d = await r.json();
      if (!r.ok || !d.url) { toast.error(d.detail || "Gagal mengambil file"); return; }
      window.open(d.url, "_blank", "noopener");
      toast.success(`Mengunduh ${row.doc_no}`);
    } finally { setDlId(null); }
  };

  const preview = async (row) => {
    setPvId(row.id);
    try {
      const r = await api(`/api/v2/archive/${row.id}/preview`);
      const d = await r.json();
      if (!r.ok || !d.url) { toast.error(d.detail || "Gagal memuat pratinjau"); return; }
      window.open(d.url, "_blank", "noopener");
    } finally { setPvId(null); }
  };

  return (
    <div className="space-y-5" data-testid="v2-arsip-page">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <FolderArchive className="w-6 h-6 text-primary" /> Arsip Dokumen
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Arsip PDF dokumen resmi (immutable) tersimpan di object storage. Filter, cari, dan unduh.
        </p>
      </div>

      <Card className="p-4 rounded-2xl">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">Jenis Dokumen</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="arsip-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Semua Jenis</SelectItem>
                {types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Dari Tanggal</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="arsip-date-from" />
          </div>
          <div>
            <Label className="text-xs">Sampai Tanggal</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="arsip-date-to" />
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">Cari No. Dokumen</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="cth: INV-2606" className="pl-8" data-testid="arsip-search" />
            </div>
          </div>
          <Button variant="ghost" onClick={reset} data-testid="arsip-reset"><X className="w-4 h-4 mr-1" />Reset</Button>
        </div>
      </Card>

      <Card className="p-5 rounded-2xl" ref={listTopRef}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Dokumen Terarsip</h3>
          <Badge variant="outline" data-testid="arsip-count">{meta.total}</Badge>
        </div>
        <TableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No. Dokumen</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Region</TableHead>
                <TableHead>Tanggal Arsip</TableHead>
                <TableHead className="text-right">Size</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell></TableRow>}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8 text-sm">Tidak ada arsip yang cocok.</TableCell></TableRow>
              )}
              {!loading && items.map((row) => (
                <TableRow key={row.id} data-testid="arsip-row">
                  <TableCell className="font-mono text-xs font-medium">{row.doc_no}</TableCell>
                  <TableCell><Badge variant="secondary">{row.doc_type_label}</Badge></TableCell>
                  <TableCell>{row.region}</TableCell>
                  <TableCell className="text-sm">{fmtDate(row.created_at)}</TableCell>
                  <TableCell className="text-right text-muted-foreground text-sm">{fmtSize(row.size_bytes)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="ghost" disabled={pvId === row.id} onClick={() => preview(row)} data-testid="arsip-preview">
                        {pvId === row.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                        <span className="ml-1">Preview</span>
                      </Button>
                      <Button size="sm" variant="outline" disabled={dlId === row.id} onClick={() => download(row)} data-testid="arsip-download">
                        {dlId === row.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        <span className="ml-1">Unduh</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
        <ListPager
          page={page} pageSize={pageSize} total={meta.total} pages={meta.pages} loading={loading}
          onPageChange={goPage} onPageSizeChange={changePageSize}
          storageKey="archive" testid="arsip-pager"
        />
      </Card>
    </div>
  );
}
